/**
 * ProjectsListView — the project drill-in half of /admin/projects, and the
 * admin membership WRITE surface under RBAC v2.1.
 *
 * v2.1: the admin tier may add / remove / re-role ANY member at ANY level
 * (view / collaborate / plan) on any project IN THEIR ORG, and may reassign the
 * single protected OWNER (projects.owner_id). The backend authorizes all of
 * this even though admin still 404s on project DATA routes — so this admin-area
 * roster, not the (inaccessible) ProjectDetailPage, is where an admin manages
 * membership. The in-project owner panel (ProjectMembersPanel) is mirrored here:
 * role-option gating via is_planner, owner immutability + reassignment, per-row
 * busy flags, sonner toasts, the same role labels/badges.
 *
 * One row per project (name / code / status / member_count); expanding a row
 * shows that project's roster with the write controls. Consumes the ENRICHED
 * projects-with-members snapshot (the field-naming law: project_role / org_role
 * / name / email, plus v2.1 project.owner_id and member.is_planner).
 */
import React, { useState, useCallback } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Plus,
  Trash2,
  Loader2,
  Crown,
} from 'lucide-react';
import { toast } from 'sonner';
import { PROJECT_ROLE, GRANTABLE_PROJECT_ROLES } from '../../../lib/auth';

const ROLE_BADGE_CLASS = {
  [PROJECT_ROLE.VIEW]: 'bg-slate-100 text-slate-700',
  [PROJECT_ROLE.COLLABORATE]: 'bg-blue-100 text-blue-700',
  [PROJECT_ROLE.PLAN]: 'bg-emerald-100 text-emerald-700',
};

const ROLE_LABELS = {
  [PROJECT_ROLE.VIEW]: 'View',
  [PROJECT_ROLE.COLLABORATE]: 'Collaborate',
  [PROJECT_ROLE.PLAN]: 'Planner',
};

const ALL_ROLE_OPTIONS = GRANTABLE_PROJECT_ROLES.map((value) => ({
  value,
  label: ROLE_LABELS[value] || value,
}));

const roleLabel = (role) => ROLE_LABELS[role] || role;

// Options a given user may be granted: 'plan' (co-planner) only if they hold the
// org planner grant — mirrors the API precondition (granting plan to a
// non-planner 400s). Mirrors ProjectMembersPanel.roleOptionsFor.
const roleOptionsFor = (isPlanner) =>
  ALL_ROLE_OPTIONS.filter((opt) => opt.value !== PROJECT_ROLE.PLAN || isPlanner);

// As above, but always keep the member's CURRENT project_role selectable (so a
// co-planner whose org grant was later revoked still shows 'plan' and can be
// demoted). Reads the snapshot's project_role / is_planner fields.
const roleOptionsForMember = (member) => {
  const opts = roleOptionsFor(member.is_planner);
  if (!opts.some((o) => o.value === member.project_role)) {
    opts.push({ value: member.project_role, label: roleLabel(member.project_role) });
  }
  return opts;
};

const STATUS_BADGE_CLASS = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  on_hold: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-blue-50 text-blue-700 border-blue-200',
  archived: 'bg-slate-100 text-slate-500 border-slate-200',
};

const formatStatus = (status) =>
  (status || '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ') || '—';

const errorMessage = (error, fallback) =>
  error?.response?.data?.detail || fallback;

/**
 * @param {{ projects: Array, searchQuery?: string, refetch: () => Promise }} props
 *   refetch re-pulls the projects-with-members snapshot after a mutation so the
 *   roster (and member_count) re-render with server truth.
 */
const ProjectsListView = ({ projects, searchQuery = '', refetch }) => {
  const { user } = useAuth();
  const myId = user?.id;
  const [expandedId, setExpandedId] = useState(null);

  // Per-row in-flight flags keyed by user_id so one row's spinner/disabled state
  // is isolated (mirrors ProjectMembersPanel.rowBusy).
  const [rowBusy, setRowBusy] = useState({});

  // Add-member dialog state (scoped to the project whose button was clicked).
  const [addState, setAddState] = useState(null);
  // null | { project, invitable, selectedUserId, selectedRole, busy }

  // Reassign-owner dialog state.
  const [reassignState, setReassignState] = useState(null);
  // null | { project, planners, selectedUserId, busy }

  const setBusy = (userId, value) =>
    setRowBusy((prev) => ({ ...prev, [userId]: value }));

  const reload = useCallback(async () => {
    if (refetch) await refetch();
  }, [refetch]);

  // ---- Add member ---------------------------------------------------------
  const openAddDialog = async (project) => {
    setAddState({
      project,
      invitable: [],
      selectedUserId: '',
      selectedRole: PROJECT_ROLE.VIEW,
      busy: false,
      loadingUsers: true,
    });
    try {
      const res = await api.get(`/projects/${project.id}/members/invitable`);
      setAddState((s) =>
        s && s.project.id === project.id
          ? { ...s, invitable: res.data || [], loadingUsers: false }
          : s
      );
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to load users'));
      setAddState((s) =>
        s && s.project.id === project.id ? { ...s, loadingUsers: false } : s
      );
    }
  };

  const submitAdd = async () => {
    if (!addState?.selectedUserId) {
      toast.error('Select a user to add');
      return;
    }
    const { project, selectedUserId, selectedRole } = addState;
    setAddState((s) => ({ ...s, busy: true }));
    try {
      await api.post(`/projects/${project.id}/members`, {
        user_id: selectedUserId,
        role: selectedRole,
      });
      toast.success('Member added');
      setAddState(null);
      await reload();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to add member'));
      setAddState((s) => (s ? { ...s, busy: false } : s));
    }
  };

  // ---- Role change / remove ----------------------------------------------
  const handleRoleChange = async (project, member, role) => {
    if (role === member.project_role) return;
    setBusy(member.user_id, true);
    try {
      await api.patch(`/projects/${project.id}/members/${member.user_id}`, { role });
      toast.success('Role updated');
      await reload();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to update role'));
    } finally {
      setBusy(member.user_id, false);
    }
  };

  const handleRemove = async (project, member) => {
    setBusy(member.user_id, true);
    try {
      await api.delete(`/projects/${project.id}/members/${member.user_id}`);
      toast.success('Member removed');
      await reload();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to remove member'));
    } finally {
      setBusy(member.user_id, false);
    }
  };

  // ---- Reassign owner -----------------------------------------------------
  const openReassignDialog = async (project) => {
    setReassignState({
      project,
      planners: [],
      selectedUserId: '',
      busy: false,
      loadingUsers: true,
    });
    try {
      const res = await api.get(`/projects/${project.id}/members/invitable`);
      // New owner must hold the org planner grant (backend 400s otherwise).
      // Candidates = the project's EXISTING planner members (co-planners — the
      // most natural new owners) UNION the invitable org planners (not yet on
      // the project). /invitable excludes current members, so co-planners must
      // come from the snapshot. Exclude the current owner; dedupe by id.
      const invitablePlanners = (res.data || [])
        .filter((u) => u.is_planner)
        .map((u) => ({ id: u.id, name: u.name, email: u.email }));
      const memberPlanners = (project.members || [])
        .filter((m) => m.is_planner && String(m.user_id) !== String(project.owner_id))
        .map((m) => ({ id: m.user_id, name: m.name, email: m.email }));
      const byId = new Map();
      for (const c of [...memberPlanners, ...invitablePlanners]) {
        // Exclude the acting admin: an admin may not reassign a project to
        // themselves (the backend 403s this — escalation guard). Deny-by-hide.
        if (String(c.id) === String(myId)) continue;
        if (!byId.has(c.id)) byId.set(c.id, c);
      }
      const planners = Array.from(byId.values()).sort((a, b) =>
        (a.name || a.email || '').localeCompare(b.name || b.email || '')
      );
      setReassignState((s) =>
        s && s.project.id === project.id
          ? { ...s, planners, loadingUsers: false }
          : s
      );
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to load users'));
      setReassignState((s) =>
        s && s.project.id === project.id ? { ...s, loadingUsers: false } : s
      );
    }
  };

  const submitReassign = async () => {
    if (!reassignState?.selectedUserId) {
      toast.error('Select a new owner');
      return;
    }
    const { project, selectedUserId } = reassignState;
    setReassignState((s) => ({ ...s, busy: true }));
    try {
      await api.put(`/projects/${project.id}/owner`, { new_owner_id: selectedUserId });
      toast.success('Owner reassigned');
      setReassignState(null);
      await reload();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to reassign owner'));
      setReassignState((s) => (s ? { ...s, busy: false } : s));
    }
  };

  const q = searchQuery.trim().toLowerCase();
  const visible = q
    ? projects.filter(
        (p) =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.code || '').toLowerCase().includes(q)
      )
    : projects;

  const toggle = (id) => setExpandedId((cur) => (cur === id ? null : id));

  // Add-dialog role options gate 'plan' on the selected user's org planner grant.
  const addSelectedUser = addState?.invitable.find(
    (u) => u.id === addState.selectedUserId
  );
  const addRoleOptions = roleOptionsFor(!!addSelectedUser?.is_planner);

  return (
    <Card data-testid="projects-list-view">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]" />
              <TableHead>Project</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Members</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-slate-500">
                  No projects found.
                </TableCell>
              </TableRow>
            ) : (
              visible.map((p) => {
                const expanded = expandedId === p.id;
                return (
                  <React.Fragment key={p.id}>
                    <TableRow
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => toggle(p.id)}
                      data-testid={`project-row-${p.id}`}
                    >
                      <TableCell className="text-slate-400">
                        {expanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {p.name}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {p.code || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={STATUS_BADGE_CLASS[p.status] || ''}
                        >
                          {formatStatus(p.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-slate-600">
                        {p.member_count}
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                        <TableCell colSpan={5} className="p-4">
                          {/* Roster header with Add Member */}
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-semibold text-slate-900">
                              Members
                            </p>
                            <Button
                              size="sm"
                              className="h-8 gap-2 bg-slate-900 hover:bg-slate-800"
                              onClick={(e) => {
                                e.stopPropagation();
                                openAddDialog(p);
                              }}
                              data-testid={`admin-add-member-${p.id}`}
                            >
                              <Plus className="h-4 w-4" />
                              Add Member
                            </Button>
                          </div>
                          {(p.members || []).length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-6 text-center">
                              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                                <FolderOpen className="h-6 w-6 text-slate-300" />
                              </div>
                              <p className="text-sm font-medium text-slate-900 mb-1">
                                No members yet
                              </p>
                              <p className="text-xs text-slate-400">
                                Add a member to grant project access.
                              </p>
                            </div>
                          ) : (
                            <Table data-testid={`project-roster-${p.id}`}>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Name</TableHead>
                                  <TableHead>Email</TableHead>
                                  <TableHead className="w-[180px]">
                                    Project Role
                                  </TableHead>
                                  <TableHead className="w-[120px] text-right">
                                    Actions
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {(p.members || []).map((m) => {
                                  const isOwner =
                                    !!p.owner_id && String(m.user_id) === String(p.owner_id);
                                  const busy = !!rowBusy[m.user_id];
                                  return (
                                    <TableRow
                                      key={m.user_id}
                                      data-testid={`admin-member-row-${m.user_id}`}
                                    >
                                      <TableCell className="font-medium text-slate-900">
                                        {m.name || '—'}
                                      </TableCell>
                                      <TableCell className="text-slate-500">
                                        {m.email || '—'}
                                      </TableCell>
                                      <TableCell>
                                        {isOwner ? (
                                          <Badge
                                            className={`${ROLE_BADGE_CLASS[PROJECT_ROLE.PLAN]} text-[10px]`}
                                            data-testid={`admin-member-role-${m.user_id}`}
                                          >
                                            Owner
                                          </Badge>
                                        ) : (
                                          <Select
                                            value={m.project_role}
                                            onValueChange={(role) =>
                                              handleRoleChange(p, m, role)
                                            }
                                            disabled={busy}
                                          >
                                            <SelectTrigger
                                              className="h-8"
                                              data-testid={`admin-member-role-${m.user_id}`}
                                            >
                                              <SelectValue>
                                                <Badge
                                                  className={`${ROLE_BADGE_CLASS[m.project_role] || ''} text-[10px]`}
                                                >
                                                  {roleLabel(m.project_role)}
                                                </Badge>
                                              </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                              {roleOptionsForMember(m).map((opt) => (
                                                <SelectItem
                                                  key={opt.value}
                                                  value={opt.value}
                                                >
                                                  {opt.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {isOwner ? (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 gap-1.5 px-2 text-slate-500 hover:text-slate-900"
                                            onClick={() => openReassignDialog(p)}
                                            data-testid={`admin-reassign-owner-${p.id}`}
                                          >
                                            <Crown className="h-4 w-4" />
                                            <span className="text-xs">
                                              Reassign
                                            </span>
                                          </Button>
                                        ) : (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                                            onClick={() => handleRemove(p, m)}
                                            disabled={busy}
                                            data-testid={`admin-remove-member-${m.user_id}`}
                                            aria-label="Remove member"
                                          >
                                            {busy ? (
                                              <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                              <Trash2 className="h-4 w-4" />
                                            )}
                                          </Button>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* Add Member Dialog */}
      <Dialog
        open={!!addState}
        onOpenChange={(open) => {
          if (!open && !addState?.busy) setAddState(null);
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">
              Add Project Member
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {addState?.project
                ? `Grant a user a role on ${addState.project.name}.`
                : 'Grant a user a role on this project.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-900">User</label>
              <Select
                value={addState?.selectedUserId || ''}
                onValueChange={(v) =>
                  setAddState((s) => ({
                    ...s,
                    selectedUserId: v,
                    selectedRole: PROJECT_ROLE.VIEW, // reset; 'plan' may not be valid for new user
                  }))
                }
              >
                <SelectTrigger
                  className="h-9"
                  data-testid="admin-add-member-user-select"
                >
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {addState?.loadingUsers ? (
                    <div className="px-2 py-1.5 text-sm text-slate-400">
                      Loading...
                    </div>
                  ) : (addState?.invitable || []).length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-slate-400">
                      No users available
                    </div>
                  ) : (
                    (addState?.invitable || []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-900">
                Project Role
              </label>
              <Select
                value={addState?.selectedRole || PROJECT_ROLE.VIEW}
                onValueChange={(v) =>
                  setAddState((s) => ({ ...s, selectedRole: v }))
                }
              >
                <SelectTrigger
                  className="h-9"
                  data-testid="admin-add-member-role-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {addRoleOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAddState(null)}
              disabled={addState?.busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-slate-900 hover:bg-slate-800"
              onClick={submitAdd}
              disabled={addState?.busy || !addState?.selectedUserId}
              data-testid="admin-add-member-submit"
            >
              {addState?.busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Member'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Owner Dialog */}
      <Dialog
        open={!!reassignState}
        onOpenChange={(open) => {
          if (!open && !reassignState?.busy) setReassignState(null);
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">
              Reassign Owner
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              The new owner must hold the organization planner grant. Only org
              planners are listed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-900">
                New Owner
              </label>
              <Select
                value={reassignState?.selectedUserId || ''}
                onValueChange={(v) =>
                  setReassignState((s) => ({ ...s, selectedUserId: v }))
                }
              >
                <SelectTrigger
                  className="h-9"
                  data-testid="admin-reassign-owner-select"
                >
                  <SelectValue placeholder="Select a new owner" />
                </SelectTrigger>
                <SelectContent>
                  {reassignState?.loadingUsers ? (
                    <div className="px-2 py-1.5 text-sm text-slate-400">
                      Loading...
                    </div>
                  ) : (reassignState?.planners || []).length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-slate-400">
                      No eligible planners
                    </div>
                  ) : (
                    (reassignState?.planners || []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setReassignState(null)}
              disabled={reassignState?.busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-slate-900 hover:bg-slate-800"
              onClick={submitReassign}
              disabled={reassignState?.busy || !reassignState?.selectedUserId}
              data-testid="admin-reassign-owner-submit"
            >
              {reassignState?.busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Reassigning...
                </>
              ) : (
                'Reassign'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ProjectsListView;
